// Service to fetch ski areas from the backend API
import { getIdToken } from '../contexts/AuthContext';
import { buildApiUrl, getAuthHeaders } from '../config/api';

/**
 * Fetches all ski areas from the backend API
 * @returns {Promise<Array>} Array of ski area objects
 */
export const fetchSkiAreas = async () => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl('/ski-areas'), {
      headers: getAuthHeaders(idToken)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch ski areas: ${response.statusText}`);
    }
    const skiAreas = await response.json();
    return skiAreas;
  } catch (error) {
    console.error('Error fetching ski areas:', error);
    throw error;
  }
};

/**
 * Fetches the DAG (directed acyclic graph) for a specific ski area
 * @param {string} skiAreaId - The ski area ID
 * @returns {Promise<Object>} DAG object with nodes, edges, and stats
 */
export const fetchSkiAreaDag = async (skiAreaId) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/ski-areas/${encodeURIComponent(skiAreaId)}/dag`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch DAG: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching DAG for ${skiAreaId}:`, error);
    throw error;
  }
};

/**
 * Fetches a specific ski area by ID
 * @param {string} skiAreaId - The ski area ID
 * @returns {Promise<Object>} Ski area object
 */
export const fetchSkiAreaById = async (skiAreaId) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/ski-areas/${encodeURIComponent(skiAreaId)}`), {
      headers: getAuthHeaders(idToken)
    });
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Ski area not found');
      }
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch ski area: ${response.statusText}`);
    }
    const skiArea = await response.json();
    return skiArea;
  } catch (error) {
    console.error(`Error fetching ski area ${skiAreaId}:`, error);
    throw error;
  }
};

/**
 * Fetches run geometry for a specific ski area
 * @param {string} skiAreaId - The ski area ID
 * @returns {Promise<Object>} GeoJSON FeatureCollection
 */
export const fetchSkiAreaRuns = async (skiAreaId) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/ski-areas/${encodeURIComponent(skiAreaId)}/runs`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch runs: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching runs for ${skiAreaId}:`, error);
    throw error;
  }
};

/**
 * Fetches lift geometry for a specific ski area
 * @param {string} skiAreaId - The ski area ID
 * @returns {Promise<Object>} GeoJSON FeatureCollection
 */
export const fetchSkiAreaLifts = async (skiAreaId) => {
  try {
    const idToken = await getIdToken();
    if (!idToken) {
      throw new Error('Not authenticated. Please log in.');
    }

    const response = await fetch(buildApiUrl(`/ski-areas/${encodeURIComponent(skiAreaId)}/lifts`), {
      headers: getAuthHeaders(idToken)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: response.statusText }));
      throw new Error(errorData.message || errorData.error || `Failed to fetch lifts: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error(`Error fetching lifts for ${skiAreaId}:`, error);
    throw error;
  }
};
